import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import API from "../config/API";
import "./PaymentMethod.css";

function PaymentMethod() {
  const location = useLocation();
  const navigate = useNavigate();

  const user = JSON.parse(localStorage.getItem("user"));

  const booking = location.state?.booking || {};
  const razorpay = location.state?.razorpay || {};

  // -----------------------------------
  // STATES
  // -----------------------------------

  const [mobileNumber, setMobileNumber] = useState(booking.mobileNumber || "");

  const [upiId, setUpiId] = useState("");

  const [sending, setSending] = useState(false);

  const [paymentStatus, setPaymentStatus] = useState("PENDING");

  const [statusMessage, setStatusMessage] = useState(
    "Choose your payment method",
  );

  // 5 minutes
  const [seconds, setSeconds] = useState(300);

  const [linkSent, setLinkSent] = useState(false);

  // -----------------------------------
  // REFS
  // -----------------------------------

  // 1 second timer
  const timerRef = useRef(null);

  // Razorpay payment status polling
  const paymentPollingRef = useRef(null);

  // Bed status polling - every 15 seconds
  const bedStatusIntervalRef = useRef(null);

  // 5 minute timeout
  const bedStatusTimeoutRef = useRef(null);

  // Prevent multiple update calls
  const updatingBedRef = useRef(false);

  // -----------------------------------
  // FORMAT TIMER
  // -----------------------------------

  const formatTime = () => {
    const minutes = Math.floor(seconds / 60);

    const remainingSeconds = seconds % 60;

    return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
  };

  // -----------------------------------
  // PAYMENT TIMER
  // -----------------------------------

  useEffect(() => {
    if (!linkSent) {
      return;
    }

    // Start 5 minute countdown
    timerRef.current = setInterval(() => {
      setSeconds((previous) => {
        if (previous <= 1) {
          if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
          }

          return 0;
        }

        return previous - 1;
      });
    }, 1000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);

        timerRef.current = null;
      }
    };
  }, [linkSent]);

  // -----------------------------------
  // SEND PAYMENT LINK
  // -----------------------------------

  const sendPaymentLink = async () => {
    if (mobileNumber.length !== 10) {
      alert("Please enter valid 10 digit mobile number.");

      return;
    }

    if (!booking.bedId) {
      alert("Bed ID not available.");

      return;
    }

    try {
      setSending(true);

      const payload = {
        bookingId: booking.userBookingId,

        registerId: booking.registerId,

        bedId: booking.bedId,

        amount: booking.totalAmount,

        mobileNumber: mobileNumber,

        paymentMode: "RAZORPAY",

        createdBy: booking.createdBy || user?.emailId,
      };

      console.log("Payment Link Payload:", payload);

      const response = await axios.post(
        `${API.V2}/payment/createPaymentLink`,

        payload,

        {
          headers: {
            "Content-Type": "application/json",

            Authorization: `Bearer ${user?.jwtToken}`,
          },
        },
      );

      console.log("Payment Link Response:", response.data);

      if (response.data?.statusCode === 200) {
        setLinkSent(true);

        setSeconds(300);

        setPaymentStatus("PENDING");

        setStatusMessage(
          "Payment link sent successfully. Waiting for payment...",
        );

        alert("Payment link sent successfully to " + mobileNumber);
      } else {
        alert(response.data?.message || "Unable to send payment link.");
      }
    } catch (error) {
      console.error("Payment Link Error:", error);

      alert(error.response?.data?.message || "Unable to send payment link.");
    } finally {
      setSending(false);
    }
  };

  // -----------------------------------
  // CHECK RAZORPAY PAYMENT STATUS
  // -----------------------------------

  const checkPaymentStatus = async () => {
    try {
      const paymentLinkId = razorpay?.paymentLinkId;

      if (!paymentLinkId) {
        console.log("Payment Link ID not available");

        return;
      }

      const response = await axios.get(
        `${API.V2}/payment/status/${paymentLinkId}`,

        {
          headers: {
            Authorization: `Bearer ${user?.jwtToken}`,
          },
        },
      );

      console.log("Payment Status Response:", response.data);

      if (response.data?.statusCode === 200) {
        const status = response.data?.data?.paymentStatus;

        console.log("Razorpay Payment Status:", status);

        if (status === "SUCCESS" || status === "PAID") {
          // Stop payment polling
          if (paymentPollingRef.current) {
            clearInterval(paymentPollingRef.current);

            paymentPollingRef.current = null;
          }

          // Stop bed polling
          if (bedStatusIntervalRef.current) {
            clearInterval(bedStatusIntervalRef.current);

            bedStatusIntervalRef.current = null;
          }

          // Stop 5 minute timeout
          if (bedStatusTimeoutRef.current) {
            clearTimeout(bedStatusTimeoutRef.current);

            bedStatusTimeoutRef.current = null;
          }

          // Stop countdown
          if (timerRef.current) {
            clearInterval(timerRef.current);

            timerRef.current = null;
          }

          setPaymentStatus("SUCCESS");

          setStatusMessage("Payment Successfully Received");

          console.log("Payment successful.");
        }
      }
    } catch (error) {
      console.error("Payment status error:", error);
    }
  };

  // -----------------------------------
  // CHECK BED STATUS
  // -----------------------------------

  const checkBedStatus = async () => {
    try {
      const bedId = booking?.bedId;

      if (!bedId) {
        console.log("Bed ID not available");

        return null;
      }

      const response = await axios.get(
        `${API.V2}/pgOwner/getBedStatus?bedId=${bedId}`,

        {
          headers: {
            Authorization: `Bearer ${user?.jwtToken}`,
          },
        },
      );

      console.log("Bed Status API Response:", response.data);

      if (response.data?.statusCode === 200) {
        /*
         * Adjust this according to your
         * backend response structure.
         */

        const bedStatus =
          response.data?.data?.bedStatus || response.data?.data?.status;

        console.log("Current Bed Status:", bedStatus);

        // --------------------------------
        // BED BOOKED
        // --------------------------------

        if (bedStatus === "BOOKED") {
          console.log("BED IS BOOKED");

          // Stop bed polling
          if (bedStatusIntervalRef.current) {
            clearInterval(bedStatusIntervalRef.current);

            bedStatusIntervalRef.current = null;
          }

          // Stop 5 minute timeout
          if (bedStatusTimeoutRef.current) {
            clearTimeout(bedStatusTimeoutRef.current);

            bedStatusTimeoutRef.current = null;
          }

          // Stop countdown
          if (timerRef.current) {
            clearInterval(timerRef.current);

            timerRef.current = null;
          }

          setPaymentStatus("SUCCESS");

          setStatusMessage("Bed booked successfully.");

          // Navigate dashboard
          setTimeout(() => {
            navigate("/usersDashboard");
          }, 1000);

          return "BOOKED";
        }

        // --------------------------------
        // BOOKING HOLD
        // --------------------------------

        if (bedStatus === "BOOKING_HOLD") {
          console.log("BED IS BOOKING_HOLD");

          return "BOOKING_HOLD";
        }

        // --------------------------------
        // AVAILABLE
        // --------------------------------

        if (bedStatus === "AVAILABLE") {
          console.log("BED IS AVAILABLE");

          return "AVAILABLE";
        }

        return bedStatus;
      }

      return null;
    } catch (error) {
      console.error("Check Bed Status Error:", error);

      return null;
    }
  };

  // -----------------------------------
  // UPDATE / DELETE BOOKING HOLD
  // -----------------------------------

  const updateBedStatus = async () => {
    try {
      // Prevent duplicate API calls
      if (updatingBedRef.current) {
        console.log("Update bed status already in progress.");

        return;
      }

      const bedId = booking?.bedId;

      if (!bedId) {
        console.log("Bed ID not available.");

        return;
      }

      updatingBedRef.current = true;

      console.log("Calling updateBedStatus for bedId:", bedId);

      /*
       * Your backend currently uses:
       *
       * @GetMapping("/updateBedStatus")
       *
       */

      const response = await axios.get(
        `${API.V1}/pgOwner/updateBedStatus?bedId=${bedId}`,

        {
          headers: {
            Authorization: `Bearer ${user?.jwtToken}`,
          },
        },
      );

      console.log("Update Bed Status Response:", response.data);

      if (response.data?.statusCode === 200) {
        setPaymentStatus("EXPIRED");

        setStatusMessage(
          "Payment time expired. Booking hold has been released.",
        );

        console.log("Booking hold released successfully.");
      } else {
        console.log(response.data?.message || "Unable to update bed status.");
      }
    } catch (error) {
      console.error("Update Bed Status Error:", error);

      console.error("Backend Error:", error.response?.data);
    } finally {
      updatingBedRef.current = false;
    }
  };

  // -----------------------------------
  // BED STATUS POLLING
  // EVERY 15 SECONDS
  // MAXIMUM 5 MINUTES
  // -----------------------------------

  useEffect(() => {
    if (!linkSent) {
      return;
    }

    console.log("Payment link sent.");

    console.log("Starting bed status checking...");

    // --------------------------------
    // IMMEDIATE CHECK
    // --------------------------------

    checkBedStatus();

    // --------------------------------
    // EVERY 15 SECONDS
    // --------------------------------

    bedStatusIntervalRef.current = setInterval(() => {
      console.log("Checking bed status...");

      checkBedStatus();
    }, 15000);

    // --------------------------------
    // AFTER 5 MINUTES
    // --------------------------------

    bedStatusTimeoutRef.current = setTimeout(
      async () => {
        console.log("5 minutes completed.");

        console.log("Stopping 15 second bed polling.");

        // Stop 15-second polling
        if (bedStatusIntervalRef.current) {
          clearInterval(bedStatusIntervalRef.current);

          bedStatusIntervalRef.current = null;
        }

        // --------------------------------
        // FINAL BED STATUS CHECK
        // --------------------------------

        console.log("Performing final bed status check...");

        const finalStatus = await checkBedStatus();

        console.log("Final Bed Status:", finalStatus);

        // --------------------------------
        // STILL BOOKING HOLD
        // --------------------------------

        if (finalStatus === "BOOKING_HOLD") {
          console.log("Bed is still BOOKING_HOLD after 5 minutes.");

          // Delete/release booking
          await updateBedStatus();
        }
      },
      5 * 60 * 1000,
    );

    // --------------------------------
    // CLEANUP
    // --------------------------------

    return () => {
      console.log("Cleaning bed status timers...");

      if (bedStatusIntervalRef.current) {
        clearInterval(bedStatusIntervalRef.current);

        bedStatusIntervalRef.current = null;
      }

      if (bedStatusTimeoutRef.current) {
        clearTimeout(bedStatusTimeoutRef.current);

        bedStatusTimeoutRef.current = null;
      }
    };
  }, [linkSent]);

  // -----------------------------------
  // PAYMENT STATUS POLLING
  // EVERY 3 SECONDS
  // -----------------------------------

  useEffect(() => {
    if (!linkSent) {
      return;
    }

    paymentPollingRef.current = setInterval(() => {
      checkPaymentStatus();
    }, 3000);

    return () => {
      if (paymentPollingRef.current) {
        clearInterval(paymentPollingRef.current);

        paymentPollingRef.current = null;
      }
    };
  }, [linkSent]);

  // -----------------------------------
  // RETRY PAYMENT
  // -----------------------------------

  const retryPayment = () => {
    console.log("Retrying payment...");

    // Clear timer
    if (timerRef.current) {
      clearInterval(timerRef.current);

      timerRef.current = null;
    }

    // Clear bed polling
    if (bedStatusIntervalRef.current) {
      clearInterval(bedStatusIntervalRef.current);

      bedStatusIntervalRef.current = null;
    }

    // Clear bed timeout
    if (bedStatusTimeoutRef.current) {
      clearTimeout(bedStatusTimeoutRef.current);

      bedStatusTimeoutRef.current = null;
    }

    setSeconds(300);

    setLinkSent(false);

    setPaymentStatus("PENDING");

    setStatusMessage("Enter mobile number and send payment link.");
  };

  // -----------------------------------
  // RENDER
  // -----------------------------------

  return (
    <div className="payment-method-page">
      <div className="payment-method-container">
        {/* HEADER */}

        <div className="payment-header">
          <button className="back-btn" onClick={() => navigate(-1)}>
            ←
          </button>

          <h2>Payment</h2>
        </div>

        {/* BOOKING DETAILS */}

        <div className="payment-card">
          <h3>BOOKING DETAILS</h3>

          <div className="detail-row">
            <span>Register ID</span>

            <strong>{booking.registerId}</strong>
          </div>

          <div className="detail-row">
            <span>Bed</span>

            <strong>{booking.bedId}</strong>
          </div>

          <div className="detail-row">
            <span>Payment Type</span>

            <strong>{booking.paymentType}</strong>
          </div>

          <div className="detail-row">
            <span>Staying Days</span>

            <strong>{booking.stayingDays || "-"}</strong>
          </div>
        </div>

        {/* PRICE DETAILS */}

        <div className="payment-card">
          <h3>PRICE DETAILS</h3>

          <div className="detail-row">
            <span>Room Amount</span>

            <strong>₹ {booking.amount || 0}</strong>
          </div>

          <div className="detail-row">
            <span>Advance Amount</span>

            <strong>₹ {booking.advanceAmount || 0}</strong>
          </div>

          <div className="total-row">
            <span>Total Amount</span>

            <strong>₹ {booking.totalAmount || 0}</strong>
          </div>
        </div>

        {/* PAYMENT METHOD */}

        <div className="payment-card">
          <h3>PAYMENT METHOD</h3>

          <div className="upi-box">
            <div className="upi-icon">UPI</div>

            <div>
              <strong>UPI Payment</strong>

              <p>Google Pay • PhonePe • Paytm • BHIM</p>
            </div>
          </div>

          {/* UPI ID */}

          <label>UPI ID</label>

          <input
            type="text"
            value={upiId}
            onChange={(event) => setUpiId(event.target.value)}
            placeholder="example@upi"
          />

          <p className="field-help">
            Optional. The customer can also select UPI directly on the Razorpay
            payment page.
          </p>

          {/* MOBILE */}

          <label>Mobile Number</label>

          <input
            type="text"
            maxLength={10}
            value={mobileNumber}
            disabled={linkSent}
            onChange={(event) => {
              const value = event.target.value.replace(/\D/g, "");

              setMobileNumber(value);
            }}
            placeholder="Enter 10 digit mobile number"
          />

          {!linkSent && (
            <button
              className="send-link-btn"
              disabled={sending || mobileNumber.length !== 10}
              onClick={sendPaymentLink}
            >
              {sending
                ? "Sending Payment Link..."
                : `Send Payment Link • ₹${booking.totalAmount}`}
            </button>
          )}
        </div>

        {/* TIMER */}

        {linkSent && paymentStatus === "PENDING" && (
          <div className="timer-card">
            <div className="timer-icon">⏱</div>

            <div>
              <h3>Payment Link Sent</h3>

              <p>Please complete the payment before the timer expires.</p>

              <div className="timer">{formatTime()}</div>
            </div>
          </div>
        )}

        {/* SUCCESS */}

        {paymentStatus === "SUCCESS" && (
          <div className="success-card">
            <div className="success-icon">✓</div>

            <h2>Payment Successfully Received</h2>

            <p>
              Your payment of ₹{booking.totalAmount} has been received
              successfully.
            </p>

            <button
              className="dashboard-btn"
              onClick={() =>
                navigate("/usersDashboardPage", {
                  state: {
                    userRegisterId: booking?.userRegisterId,
                    mobileNumber: booking?.mobileNumber,
                  },
                })
              }
            >
              Continue
            </button>
          </div>
        )}

        {/* EXPIRED */}

        {paymentStatus === "EXPIRED" && (
          <div className="expired-card">
            <h3>Payment Link Expired</h3>

            <p>The 5-minute payment window has expired.</p>

            <button className="retry-btn" onClick={retryPayment}>
              Send New Payment Link
            </button>
          </div>
        )}

        {/* STATUS */}

        {paymentStatus === "PENDING" && (
          <div className="status-message">{statusMessage}</div>
        )}

        {/* SECURITY */}

        <div className="secure-payment">
          🔒
          <div>
            <strong>Secure Payment</strong>

            <p>Payments are securely processed by Razorpay.</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PaymentMethod;
